import {Transaction} from '@/types/transaction/Transaction'
import {patch} from '@/services/infrastructure/api.service'
import emitter from '@/services/infrastructure/emitter'
import {TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import {notifyError} from '@/services/infrastructure/notification.service'
import {computed, ref} from 'vue'
import useSlideIn from '@/slideIn/slideIn.service'
import {CREATE_TRANSACTION} from '@/transaction/create-transaction'
import TransactionManager from '@/transaction/TransactionManager.vue'
import {usePanelManager} from '@tritt/multipanel'

import {useAttachDocumentService} from '@/transaction/create-transaction/AttachDocument.service'
import {checkLinesDecimalAmount} from '@/transaction/TransactionUtilities'
import {useTransactionCreator} from '@/transaction/create-transaction/TransactionCreator.service'
import {cloneFromTransactionLines, entryLinesToTransactionLines} from '@/transaction/transaction.mapper'
import {ResourceLink} from '@/resource-link/resourceLink'
import {TransactionEntryLine} from '@/types/transaction/TransactionEntryLine'
import {DocumentMeta} from '@/types/transaction/DocumentMeta'
import {listDocumentsByTransactionUuid} from '@/services/document.service'

const TRANSACTIONS_PATH = '/transactions'

const existingAttachments = ref<Array<DocumentMeta>>([])
const existingTransaction = ref<Transaction>()
const originalHash = ref('')

export const useEditTransactionCreator = () => {

    const {transactionEntryLines, reset: resetCreate, attachments, resourceLinks} = useTransactionCreator()
    const {copyAndAttachDocumentsToTransaction} = useAttachDocumentService()
    const {showSlideIn, closeSlideIn} = useSlideIn()
    const {contentExistInPanels} = usePanelManager()

    const isDirty = computed(() => {
        const hash = hashTransaction(attachments.value,existingAttachments.value, transactionEntryLines.value, resourceLinks.value)
        return hash !== originalHash.value
    })

    const reset = () => {
        resetCreate()
        originalHash.value = ''
        existingAttachments.value = []
        existingTransaction.value = undefined
    }

    const transactionFromCopy = (transaction: Transaction) => {
        reset()
        transactionEntryLines.value = cloneFromTransactionLines(transaction.transactionLines)
    }

    const editTransaction = async (transaction: Transaction) => {
        reset()
        existingTransaction.value = transaction
        transactionEntryLines.value = cloneFromTransactionLines(transaction.transactionLines)
        existingAttachments.value = (await listDocumentsByTransactionUuid(transaction.uuid)).filter(att => !att.isDeleted)
        originalHash.value = hashTransaction(attachments.value, existingAttachments.value, transactionEntryLines.value, resourceLinks.value)
    }

    const patchTransaction = async () => {
        const cmd = {
            uuid: existingTransaction.value?.uuid,
            documentMetaUuids: attachments.value.map(d => d.uuid),
            deletedDocumentMetaUuids: existingAttachments.value.filter(att => att.isDeleted).map(att => att.uuid),
            transactionLines: checkLinesDecimalAmount(entryLinesToTransactionLines(transactionEntryLines.value)),
        }
        const result = await patch(TRANSACTIONS_PATH, cmd)

        if (result.isRight()) {
            emitter.emit(TRANSACTION_UPDATED)
            reset()
        } else {
            notifyError({title: 'Feilet', description: {descriptionMessage: `Klarte ikke å oppdatere transaksjon med id: ${cmd.uuid}`, error: result.error.detail}})
        }
    }

    const openEditTransaction = async (transaction: Transaction) => {
        await editTransaction(transaction)
        if (!contentExistInPanels(CREATE_TRANSACTION.componentName)) {
            showSlideIn(TransactionManager)
        }
    }

    const openCopyTransaction = async (transaction: Transaction) => {
        transactionFromCopy(transaction)

        if (!contentExistInPanels(CREATE_TRANSACTION.componentName)) {
            showSlideIn(TransactionManager)
        }
        await copyAndAttachDocumentsToTransaction(transaction.documentMetaUuids)
    }

    return {
        patchTransaction,
        existingTransaction,
        openEditTransaction,
        openCopyTransaction,
        reset,
        isDirty,
        existingAttachments,
        closeSlideIn
    }
}

const hashTransaction = (documentMetas: DocumentMeta[], existingAttachments: DocumentMeta[], lines: TransactionEntryLine[], resourceLinks: ResourceLink[]) => {
    const docUuids = documentMetas.map((doc) => doc.uuid)
    return JSON.stringify({docUuids, existingAttachments, lines, resourceLinks})
}